import type {
  AssistantTestCampaignDto,
  AssistantTestMemberDto,
  AssistantTestNoteRequest,
  AssistantTestPurchaseDto,
  AssistantTestPurchaseRequest,
  AssistantTestPushRequest,
  AssistantTestPushTicketDto,
  AssistantTestReceiptDto,
  AssistantTestResetDto,
  AssistantTestResetRequest,
  AssistantTestRunDto,
  AssistantTestRunRequest,
} from '@signalvote/shared';
import { every } from '@signalvote/data';
import { campaignHeaders, defineQuery, defineWrite } from '@/lib/data';
import { failureFromResponse } from '@signalvote/shared';
import { apiJson, apiVoid, type ApiErrorSpec } from './apiFetch';

/** The server's own sentence when it sent one (a 409 like "no pending wake-ups" reads as is), else the fallback. */
const serverSentence =
  (fallback: string): ApiErrorSpec =>
  (body, response) =>
    failureFromResponse(response, body, body.error || body.message ? undefined : fallback);

const BASE = '/api/admin/assistant-test';

/** Platform-admin only and subsidiary-wide: the bench's campaign is a parameter, not the selected one. */
export const useAssistantTestCampaigns = defineQuery({
  key: () => ['assistantTest', 'campaigns'],
  reads: 'GET /api/admin/assistant-test/campaigns',
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<AssistantTestCampaignDto[]>(`${BASE}/campaigns`, { signal, headers: campaignHeaders(campaignId), error: 'Failed to fetch Scout campaigns' }),
});

export const useAssistantTestMembers = defineQuery({
  key: (benchCampaignId: string) => ['assistantTest', 'members', benchCampaignId],
  reads: 'GET /api/admin/assistant-test/members',
  fetch: (benchCampaignId, { signal, campaignId }) =>
    apiJson<AssistantTestMemberDto[]>(`${BASE}/members?campaignId=${encodeURIComponent(benchCampaignId)}`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch Scout members',
    }),
});

/**
 * A run saves in a child scope set to the bench's campaign, so its answer carries no write set;
 * the members (pending wake-ups) are refreshed by hand. A simulated purchase does the same.
 */
export const useAssistantTestRun = defineWrite({
  send: (body: AssistantTestRunRequest) => apiJson<AssistantTestRunDto>(`${BASE}/runs`, { method: 'POST', json: body, error: serverSentence('Failed to start the run') }),
  onSuccess: (_result, _body, refresh) => void refresh(useAssistantTestMembers, every),
});

export const useAssistantTestPush = defineWrite({
  send: (body: AssistantTestPushRequest) =>
    apiJson<AssistantTestPushTicketDto[]>(`${BASE}/push`, { method: 'POST', json: body, error: serverSentence('Failed to send the push') }),
});

export const useAssistantTestReceipts = defineWrite({
  send: (ticketIds: string[]) =>
    apiJson<AssistantTestReceiptDto[]>(`${BASE}/push/receipts`, { method: 'POST', json: { ticketIds }, error: serverSentence('Failed to read receipts') }),
});

export const useAssistantTestReset = defineWrite({
  send: (body: AssistantTestResetRequest) => apiJson<AssistantTestResetDto>(`${BASE}/reset`, { method: 'POST', json: body, error: serverSentence('Failed to reset') }),
});

export const useAssistantTestEditNote = defineWrite({
  send: ({ id, ...body }: AssistantTestNoteRequest & { id: string }) =>
    apiVoid(`${BASE}/notes/${encodeURIComponent(id)}`, { method: 'PUT', json: body, error: serverSentence('Failed to save the note') }),
});

export const useAssistantTestDeleteNote = defineWrite({
  send: ({ id, campaignId }: { id: string; campaignId: string }) =>
    apiVoid(`${BASE}/notes/${encodeURIComponent(id)}?campaignId=${encodeURIComponent(campaignId)}`, {
      method: 'DELETE',
      error: serverSentence('Failed to delete the note'),
    }),
});

export const useAssistantTestPurchase = defineWrite({
  send: (body: AssistantTestPurchaseRequest) =>
    apiJson<AssistantTestPurchaseDto>(`${BASE}/purchase`, { method: 'POST', json: body, error: serverSentence('Failed to simulate the purchase') }),
  onSuccess: (_result, _body, refresh) => void refresh(useAssistantTestMembers, every),
});
